/**
 * AdminCampaigns.js
 *
 * Sekme 1 — İndirim Kampanyaları (kupon/indirim yönetimi)
 * Sekme 2 — Kariyer Takvimi Paylaşımı (dış kitle API önizlemesi + erişim günlüğü)
 *
 * WhatsApp Kampanyası ve Şablonlar sekmeleri kaldırıldı (2026-06 yön değişikliği).
 * Kampanya gönderimi artık Kariyer Takvimi (ayrı sistem) tarafından yapılacak.
 *
 * Yeni endpoint'ler (backend eşzamanlı yazılıyor):
 *   GET /api/admin/external-share/preview?type=leads|students&status=&egitim=&course_id=&q=&limit=100&offset=0
 *     → { items:[{id,name,phone,type,status,detail,created_at}], total, with_phone, offset, limit }
 *   GET /api/admin/external-share/logs?limit=50&offset=0
 *     → { logs:[{requested_at,ip,auth_ok,filters,returned_count,total,duration_ms}], total }
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { useLeadStatusOptions } from '../hooks/useLeadStatusOptions';
import {
  Plus, Tag, X, Search, RefreshCw, AlertCircle,
  Info, Loader2, Users, Database, ExternalLink, Shield,
  CheckCircle2, XCircle, ChevronLeft, ChevronRight,
} from 'lucide-react';
import axios from 'axios';
import {
  PageHeader,
  EmptyState,
  LoadingState,
} from '../components/admin';
import { motion, AnimatePresence } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_LIMIT = 100;

// ────────────────────────────────────────────────────────────────────────────
// İNDİRİM KAMPANYALARI
// ────────────────────────────────────────────────────────────────────────────
function DiscountCampaigns({ t, lang }) {
  const { authHeaders } = useAuth();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', discount_percent: 10,
    code: '', start_date: '', end_date: '', max_usage: 100,
  });

  const fetchItems = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/campaigns`, { headers: authHeaders() });
      setItems(r.data.campaigns || []);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!form.name || !form.code) {
      toast.warning(t('admin.camp_validation'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/campaigns`, form, { headers: authHeaders() });
      toast.success(t('admin.camp_created'));
      setShowForm(false);
      setForm({ name: '', description: '', discount_percent: 10, code: '', start_date: '', end_date: '', max_usage: 100 });
      fetchItems();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = async (id, active) => {
    try {
      await axios.put(`${API}/admin/campaigns/${id}`, { is_active: !active }, { headers: authHeaders() });
      toast.success(active ? t('admin.camp_deactivated') : t('admin.camp_activated'));
      fetchItems();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowForm(true)}
          className="btn-primary px-4 py-2 text-sm flex items-center gap-2"
          data-testid="add-campaign-btn"
        >
          <Plus size={16} /> {t('admin.camp_new')}
        </button>
      </div>

      {showForm && (
        <div className="card-clay p-5 mb-6 space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="font-bold">{t('admin.camp_new')}</h3>
            <button onClick={() => setShowForm(false)}><X size={18} /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <input
              placeholder={t('admin.camp_name')}
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold text-sm"
              data-testid="camp-name-input"
            />
            <input
              placeholder={t('admin.camp_code')}
              value={form.code}
              onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
              className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold text-sm font-mono"
              data-testid="camp-code-input"
            />
          </div>
          <input
            placeholder={t('admin.camp_description')}
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)] focus:outline-none font-semibold text-sm"
          />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] font-bold text-[var(--text-muted)]">{t('admin.camp_discount')}</label>
              <input type="number" value={form.discount_percent} onChange={e => setForm({ ...form, discount_percent: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-[var(--text-muted)]">{t('admin.camp_start')}</label>
              <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-[var(--text-muted)]">{t('admin.camp_end')}</label>
              <input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-[var(--text-muted)]">{t('admin.camp_max_usage')}</label>
              <input type="number" value={form.max_usage} onChange={e => setForm({ ...form, max_usage: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm" />
            </div>
          </div>
          <button onClick={save} disabled={submitting} className="btn-success px-6 py-2 text-sm disabled:opacity-50" data-testid="save-camp-btn">
            {submitting ? t('admin.camp_creating') : t('admin.camp_create')}
          </button>
        </div>
      )}

      {loading ? (
        <LoadingState variant="table" rows={4} cols={3} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Tag size={40} strokeWidth={1.5} />}
          title={t('admin.camp_empty_title')}
          description={t('admin.camp_empty_desc')}
          action={{ label: t('admin.camp_new'), onClick: () => setShowForm(true) }}
        />
      ) : (
        <div className="space-y-3">
          {items.map(c => (
            <div key={c.campaign_id} className={`card-clay p-4 flex items-center gap-4 ${!c.is_active ? 'opacity-50' : ''}`}>
              <Tag size={20} className="text-[var(--brand-success)] flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h4 className="font-bold text-sm">{c.name}</h4>
                  <span className="px-2 py-0.5 bg-[#FFF4CC] text-[var(--brand-secondary-shadow)] rounded text-[10px] font-bold font-mono">{c.code}</span>
                  <span className="text-xs font-bold text-[var(--brand-success)]">%{c.discount_percent}</span>
                </div>
                <p className="text-xs text-[var(--text-muted)]">{c.description}{c.start_date && ` | ${c.start_date} - ${c.end_date}`}</p>
                <p className="text-[10px] text-[var(--text-muted)]">{t('admin.camp_usage')}: {c.usage_count}/{c.max_usage}</p>
              </div>
              <button
                onClick={() => toggle(c.campaign_id, c.is_active)}
                className={`px-3 py-1 rounded text-xs font-bold ${c.is_active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}
              >
                {c.is_active ? t('admin.camp_active') : t('admin.camp_inactive')}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// KARİYER TAKVİMİ PAYLAŞIMI
// ────────────────────────────────────────────────────────────────────────────

// Erişim günlüğü alt bölümü
function AccessLogs({ t, lang }) {
  const { authHeaders } = useAuth();
  const toast = useToast();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [offset, setOffset] = useState(0);
  const LOGS_LIMIT = 50;

  const fetchLogs = useCallback(async (off = 0, silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const r = await axios.get(`${API}/admin/external-share/logs?limit=${LOGS_LIMIT}&offset=${off}`, {
        headers: authHeaders(),
      });
      setLogs(r.data.logs || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => { fetchLogs(offset); }, [offset, fetchLogs]);

  const handleRefresh = () => fetchLogs(offset, true);

  const formatFilters = (filters) => {
    if (!filters || typeof filters !== 'object') return '—';
    return Object.entries(filters)
      .filter(([, v]) => v)
      .map(([k, v]) => `${k}=${v}`)
      .join(', ') || '—';
  };

  return (
    <div>
      {/* Başlık satırı */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-sm">{t('admin.ext.logs_title')}</h3>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 border-[var(--border-light)] text-xs font-bold text-[var(--text-muted)] hover:text-[var(--text-body)] hover:border-[var(--brand-primary)] transition-all disabled:opacity-50"
        >
          <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? t('admin.ext.refreshing') : t('admin.ext.refresh')}
        </motion.button>
      </div>

      {loading ? (
        <LoadingState variant="table" rows={5} cols={6} />
      ) : logs.length === 0 ? (
        <EmptyState
          icon={<Shield size={36} strokeWidth={1.5} />}
          title={t('admin.ext.log_empty_title')}
          description={t('admin.ext.log_empty_desc')}
        />
      ) : (
        <>
          <div className="card-clay overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 font-bold whitespace-nowrap">{t('admin.ext.log_col_time')}</th>
                  <th className="text-left p-3 font-bold">{t('admin.ext.log_col_ip')}</th>
                  <th className="text-left p-3 font-bold">{t('admin.ext.log_col_auth')}</th>
                  <th className="text-left p-3 font-bold">{t('admin.ext.log_col_filters')}</th>
                  <th className="text-right p-3 font-bold">{t('admin.ext.log_col_count')}</th>
                  <th className="text-right p-3 font-bold whitespace-nowrap">{t('admin.ext.log_col_duration')}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, idx) => (
                  <tr key={idx} className="border-t border-[var(--border-light)] hover:bg-gray-50">
                    <td className="p-3 font-mono text-[var(--text-muted)] whitespace-nowrap">
                      {log.requested_at
                        ? new Date(log.requested_at).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-GB', { dateStyle: 'short', timeStyle: 'medium' })
                        : '—'}
                    </td>
                    <td className="p-3 font-mono">{log.ip || '—'}</td>
                    <td className="p-3">
                      {log.auth_ok ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-bold bg-emerald-100 text-emerald-700 border-emerald-200">
                          <CheckCircle2 size={10} />
                          {t('admin.ext.log_auth_ok')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-bold bg-rose-100 text-rose-700 border-rose-200">
                          <XCircle size={10} />
                          {t('admin.ext.log_auth_fail')}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-[var(--text-muted)] max-w-[200px] truncate" title={formatFilters(log.filters)}>
                      {formatFilters(log.filters)}
                    </td>
                    <td className="p-3 text-right font-semibold">{log.returned_count ?? '—'}</td>
                    <td className="p-3 text-right font-mono text-[var(--text-muted)]">{log.duration_ms ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Sayfalama */}
          {total > LOGS_LIMIT && (
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-[var(--text-muted)] font-semibold">
                {offset + 1}–{Math.min(offset + LOGS_LIMIT, total)} / {total}
              </span>
              <div className="flex gap-2">
                <motion.button
                  whileHover={offset === 0 ? {} : { scale: 1.02 }}
                  whileTap={offset === 0 ? {} : { scale: 0.98 }}
                  onClick={() => setOffset(Math.max(0, offset - LOGS_LIMIT))}
                  disabled={offset === 0}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-xl border-2 border-[var(--border-light)] text-xs font-bold disabled:opacity-30"
                >
                  <ChevronLeft size={13} />
                  {t('admin.ext.prev_btn')}
                </motion.button>
                <motion.button
                  whileHover={offset + LOGS_LIMIT >= total ? {} : { scale: 1.02 }}
                  whileTap={offset + LOGS_LIMIT >= total ? {} : { scale: 0.98 }}
                  onClick={() => setOffset(offset + LOGS_LIMIT)}
                  disabled={offset + LOGS_LIMIT >= total}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-xl border-2 border-[var(--border-light)] text-xs font-bold disabled:opacity-30"
                >
                  {t('admin.ext.next_btn')}
                  <ChevronRight size={13} />
                </motion.button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Ana Kariyer Takvimi Paylaşımı sekmesi
function KariyerTakvimiSharing({ t, lang }) {
  const { authHeaders } = useAuth();
  const toast = useToast();
  const { options: leadStatusOptions } = useLeadStatusOptions();

  // Filtre durumu
  const [dataType, setDataType] = useState('leads');
  const [leadsStatus, setLeadsStatus] = useState('');
  const [leadsEducation, setLeadsEducation] = useState('');
  const [studentsCourseId, setStudentsCourseId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [offset, setOffset] = useState(0);

  // Veri durumu
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [withPhone, setWithPhone] = useState(0);
  const [loading, setLoading] = useState(false);
  const [courseList, setCourseList] = useState([]);

  // Debounce + AbortController
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  // Kurs listesini bir kez çek (öğrenci filtresi için)
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/courses?limit=200`, { headers: authHeaders() });
        setCourseList(r.data.courses || []);
      } catch {
        // sessiz — dropdown boş kalır
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Önizleme verisi çek
  const fetchPreview = useCallback(async (type, status, egitim, courseId, q, off) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const p = new URLSearchParams({ type, limit: String(PAGE_LIMIT), offset: String(off) });
      if (status)   p.set('status', status);
      if (egitim)   p.set('egitim', egitim);
      if (courseId) p.set('course_id', courseId);
      if (q)        p.set('q', q);
      const r = await axios.get(`${API}/admin/external-share/preview?${p.toString()}`, {
        headers: authHeaders(),
        signal: controller.signal,
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setWithPhone(r.data.with_phone || 0);
    } catch (err) {
      if (axios.isCancel(err) || err?.name === 'CanceledError') return;
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // Filtre değişince debounce ile çek; offset sıfırla
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setOffset(0);
      fetchPreview(dataType, leadsStatus, leadsEducation, studentsCourseId, searchQuery, 0);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataType, leadsStatus, leadsEducation, studentsCourseId, searchQuery]);

  // Offset değişince (sayfalama) hemen çek
  useEffect(() => {
    fetchPreview(dataType, leadsStatus, leadsEducation, studentsCourseId, searchQuery, offset);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  const totalPages = Math.ceil(total / PAGE_LIMIT);
  const currentPage = Math.floor(offset / PAGE_LIMIT) + 1;

  const formatDate = (val) => {
    if (!val) return '—';
    return new Date(val).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-GB');
  };

  return (
    <div className="space-y-6">
      {/* ── Bilgi kartı: API adresi + anahtar notu ─────────────────────────── */}
      <div className="flex items-start gap-3 p-4 rounded-2xl border-2 border-cyan-200 bg-cyan-50">
        <Info size={18} className="text-cyan-600 flex-shrink-0 mt-0.5" />
        <div className="space-y-1 text-xs font-semibold text-cyan-800 min-w-0">
          <p className="font-bold text-sm text-cyan-700">{t('admin.ext.info_title')}</p>
          <p>
            <span className="font-bold">{t('admin.ext.info_endpoint')}:</span>{' '}
            <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-cyan-200 text-cyan-900">
              /api/external/audience
            </span>
            {' '}
            <ExternalLink size={12} className="inline text-cyan-500" />
          </p>
          <p>{t('admin.ext.info_key_note')}</p>
          <p className="text-cyan-600">{t('admin.ext.info_prod_note')}</p>
        </div>
      </div>

      {/* ── Önizleme bölümü ─────────────────────────────────────────────────── */}
      <div>
        <h3 className="font-bold text-sm mb-4">{t('admin.ext.preview_title')}</h3>

        {/* Filtreler */}
        <div className="space-y-3 mb-4">
          {/* Tip radyo */}
          <div className="flex items-center gap-4">
            <span className="text-xs font-bold text-[var(--text-muted)] uppercase">{t('admin.ext.type_label')}</span>
            {[
              { val: 'leads', label: t('admin.ext.type_leads') },
              { val: 'students', label: t('admin.ext.type_students') },
            ].map(({ val, label }) => (
              <label key={val} className="flex items-center gap-2 cursor-pointer">
                <div
                  onClick={() => { setDataType(val); setLeadsStatus(''); setLeadsEducation(''); setStudentsCourseId(''); }}
                  className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${
                    dataType === val
                      ? 'border-[var(--brand-primary)] bg-[var(--brand-primary)]'
                      : 'border-[var(--border-light)] bg-white'
                  }`}
                >
                  {dataType === val && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                </div>
                <span className="text-sm font-semibold">{label}</span>
              </label>
            ))}
          </div>

          {/* Lead'e özel filtreler */}
          {dataType === 'leads' && (
            <div className="flex gap-2 flex-wrap">
              <select
                value={leadsStatus}
                onChange={e => setLeadsStatus(e.target.value)}
                className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold"
              >
                <option value="">{t('admin.ext.status_all')}</option>
                {leadStatusOptions.map(s => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <input
                value={leadsEducation}
                onChange={e => setLeadsEducation(e.target.value)}
                placeholder={t('admin.ext.education_placeholder')}
                list="ext-edu-suggestions"
                className="flex-1 min-w-[180px] px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none"
              />
              <datalist id="ext-edu-suggestions">
                {courseList.map(c => <option key={c.course_id} value={c.title} />)}
              </datalist>
            </div>
          )}

          {/* Öğrenciye özel filtre */}
          {dataType === 'students' && (
            <select
              value={studentsCourseId}
              onChange={e => setStudentsCourseId(e.target.value)}
              className="px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold min-w-[220px]"
            >
              <option value="">{t('admin.ext.course_label')}</option>
              {courseList.map(c => (
                <option key={c.course_id} value={c.course_id}>{c.title}</option>
              ))}
            </select>
          )}

          {/* Ortak arama */}
          <div className="relative max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={t('admin.ext.search_placeholder')}
              className="w-full pl-9 pr-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none"
            />
          </div>
        </div>

        {/* Özet satırı */}
        {!loading && (
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gray-100 border border-gray-200 text-xs font-bold text-[var(--text-muted)]">
              <Users size={13} />
              {t('admin.ext.summary')
                .replace('{total}', total)
                .replace('{with_phone}', withPhone)}
            </div>
            {loading && <Loader2 size={14} className="animate-spin text-[var(--brand-primary)]" />}
          </div>
        )}
        {loading && (
          <div className="flex items-center gap-2 mb-3 text-xs font-semibold text-[var(--text-muted)]">
            <Loader2 size={14} className="animate-spin text-[var(--brand-primary)]" />
            {lang === 'tr' ? 'Yükleniyor...' : 'Loading...'}
          </div>
        )}

        {/* Tablo */}
        {!loading && items.length === 0 ? (
          <EmptyState
            icon={<Database size={36} strokeWidth={1.5} />}
            title={lang === 'tr' ? 'Kayıt bulunamadı' : 'No records found'}
            description=""
          />
        ) : (
          <div className="card-clay overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 font-bold">{t('admin.ext.col_name')}</th>
                  <th className="text-left p-3 font-bold">{t('admin.ext.col_phone')}</th>
                  <th className="text-left p-3 font-bold">{t('admin.ext.col_status')}</th>
                  <th className="text-left p-3 font-bold whitespace-nowrap">{t('admin.ext.col_created')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={item.id || idx} className="border-t border-[var(--border-light)] hover:bg-gray-50">
                    <td className="p-3 font-semibold">{item.name || '—'}</td>
                    <td className="p-3 font-mono text-xs">
                      {item.phone
                        ? item.phone
                        : (
                          <span className="text-[var(--text-muted)] text-[11px]">
                            {lang === 'tr' ? 'Yok' : 'None'}
                          </span>
                        )
                      }
                    </td>
                    <td className="p-3">
                      <div className="text-xs font-semibold">{item.status || '—'}</div>
                      {item.detail && (
                        <div className="text-[11px] text-[var(--text-muted)] mt-0.5">{item.detail}</div>
                      )}
                    </td>
                    <td className="p-3 text-xs text-[var(--text-muted)]">
                      {formatDate(item.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Bilgi notu */}
        {!loading && items.length > 0 && (
          <div className="mt-3 flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200">
            <AlertCircle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs font-semibold text-amber-700">{t('admin.ext.info_note')}</p>
          </div>
        )}

        {/* Sayfalama */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-xs text-[var(--text-muted)] font-semibold">
              {currentPage} / {totalPages} ({total} {lang === 'tr' ? 'kayıt' : 'records'})
            </span>
            <div className="flex gap-2">
              <motion.button
                whileHover={offset === 0 ? {} : { scale: 1.02 }}
                whileTap={offset === 0 ? {} : { scale: 0.98 }}
                onClick={() => setOffset(Math.max(0, offset - PAGE_LIMIT))}
                disabled={offset === 0}
                className="flex items-center gap-1 px-3 py-1.5 rounded-xl border-2 border-[var(--border-light)] text-xs font-bold disabled:opacity-30"
              >
                <ChevronLeft size={13} />
                {t('admin.ext.prev_btn')}
              </motion.button>
              <motion.button
                whileHover={offset + PAGE_LIMIT >= total ? {} : { scale: 1.02 }}
                whileTap={offset + PAGE_LIMIT >= total ? {} : { scale: 0.98 }}
                onClick={() => setOffset(offset + PAGE_LIMIT)}
                disabled={offset + PAGE_LIMIT >= total}
                className="flex items-center gap-1 px-3 py-1.5 rounded-xl border-2 border-[var(--border-light)] text-xs font-bold disabled:opacity-30"
              >
                {t('admin.ext.next_btn')}
                <ChevronRight size={13} />
              </motion.button>
            </div>
          </div>
        )}
      </div>

      {/* ── Erişim günlüğü bölümü ───────────────────────────────────────────── */}
      <div className="border-t-2 border-[var(--border-light)] pt-6">
        <AccessLogs t={t} lang={lang} />
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ANA SAYFA
// ────────────────────────────────────────────────────────────────────────────
export default function AdminCampaigns() {
  const { lang, t } = useLanguage();
  const [mainTab, setMainTab] = useState('discount');

  const MAIN_TABS = [
    { key: 'discount',         label: t('admin.wa.tab_discount'),         icon: Tag          },
    { key: 'kariyertakvimi',   label: t('admin.wa.tab_kariyertakvimi'),   icon: ExternalLink },
  ];

  return (
    <AdminLayout title="Campaigns" titleTr={t('admin.camp_page_title')}>
      <PageHeader
        title={t('admin.camp_page_title')}
        subtitle={t('admin.camp_page_subtitle')}
        breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('admin.camp_page_title') }]}
      />

      {/* Ana sekmeler */}
      <div className="flex gap-2 mb-6 border-b-2 border-[var(--border-light)]">
        {MAIN_TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setMainTab(key)}
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold border-b-2 -mb-[2px] transition-all ${
              mainTab === key
                ? 'border-[var(--brand-primary)] text-[var(--brand-primary)]'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-body)]'
            }`}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={mainTab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.15 }}
        >
          {mainTab === 'discount'       && <DiscountCampaigns t={t} lang={lang} />}
          {mainTab === 'kariyertakvimi' && <KariyerTakvimiSharing t={t} lang={lang} />}
        </motion.div>
      </AnimatePresence>
    </AdminLayout>
  );
}
